import {
    AttachmentDto,
    ConsultDto,
    EntityDto,
    PatientDto,
} from 'app/models/generated'
import { consultPatientRepository } from 'app/api/consult/repository/ConsultPatientRepository'
import { ApiHelper } from 'app/api/api-helper'
import { ApiRequester } from 'app/api/api-requestor'
import { RetryConsultResponse } from 'app/api/types'
import { logEvent } from 'app/logging/firebase'
import { logger } from 'app/logging/Logger'
import { fetchVersion, executeMutation } from 'app/api/client/graphqlClient'
import {
    updateConsultDtoMutation,
    createConsultDtoMutation,
} from 'app/graphql/customQueries'

export interface ConsultRepository {
    create(consultDto: ConsultDto): Promise<ConsultDto>
    updateForState(consult: ConsultDto): Promise<ConsultDto>
    /**
     * Prepares an existing (e.g. blank) consult to be processed from a newly
     * attached audio file. Sets the consult's audioId (the transcription
     * pipeline resolves the audio via consult.audioId — an empty value fails
     * with a DynamoDB empty-key error), resets state so the pipeline's
     * idempotency check doesn't skip it, and forces HIGH accuracy so the note
     * is generated from server-side transcription of the audio.
     */
    updateForAudioAttachment(
        id: string,
        audioId: string,
        state: string,
        accuracy: string
    ): Promise<ConsultDto>
    /**
     * @deprecated Use EntityRepository.saveEntities() instead
     * This method is kept for backward compatibility during migration
     */
    updateEntities(id: string, entities: EntityDto[]): Promise<ConsultDto>
    updatePatient(id: string, patient: PatientDto): void
    updateRating(id: string, rating: number): Promise<ConsultDto>
    updatePresignedUrls(
        id: string,
        attachments: AttachmentDto[]
    ): Promise<ConsultDto>
}

export class AmplifyCreateConsultRepository implements ConsultRepository {
    async create(consultDto: ConsultDto): Promise<ConsultDto> {
        try {
            logger.debug(
                '[ConsultRepository] Creating ConsultDto via GraphQL mutation',
                {
                    action: 'create_consult_dto',
                    metadata: {
                        hasOwner: !!consultDto.owner,
                        hasAuthId: !!consultDto.authId,
                        type: consultDto.type,
                        state: consultDto.state,
                        audioId: consultDto.audioId,
                    },
                }
            )

            // Build a plain input object from the DataStore model instance.
            // We strip undefined values so AppSync doesn't reject nulls for
            // fields that aren't set.
            const input: Record<string, unknown> = {
                id: consultDto.id,
                owner: consultDto.owner,
                authId: consultDto.authId,
                patientId: consultDto.patientId,
                title: consultDto.title,
                type: consultDto.type,
                catalogueTemplateId: consultDto.catalogueTemplateId ?? null,
                catalogueTemplateVersion:
                    consultDto.catalogueTemplateVersion ?? null,
                startDate: consultDto.startDate,
                completionDate: consultDto.completionDate,
                summary: consultDto.summary,
                state: consultDto.state,
                audioId: consultDto.audioId,
                rating: consultDto.rating,
                accuracy: consultDto.accuracy,
                stateTimestamps: consultDto.stateTimestamps ?? [],
            }

            // Only include complex fields if they have values
            if (consultDto.entities?.length) {
                input.entities = consultDto.entities
            } else {
                input.entities = []
            }
            if (consultDto.template) {
                input.template = consultDto.template
            }
            if (consultDto.attachments?.length) {
                input.attachments = consultDto.attachments
            } else {
                input.attachments = []
            }
            if (consultDto.matchedPatients?.length) {
                input.matchedPatients = consultDto.matchedPatients
            } else {
                input.matchedPatients = []
            }

            const createdDto = await executeMutation<ConsultDto>(
                createConsultDtoMutation,
                { input },
                'createConsultDto'
            )

            console.error(
                '[DIAG] ConsultRepository.create: GraphQL mutation succeeded, id =',
                createdDto.id,
                'owner =',
                createdDto.owner?.substring(0, 8)
            )

            logger.debug(
                '[ConsultRepository] GraphQL createConsultDto succeeded',
                {
                    action: 'create_consult_dto_success',
                    metadata: {
                        consultId: createdDto.id,
                        // _version is AppSync conflict metadata, not on the model type
                        version: (createdDto as { _version?: number })._version,
                    },
                }
            )

            return createdDto
        } catch (error) {
            logger.error(
                '[ConsultRepository] GraphQL createConsultDto FAILED',
                error as Error,
                {
                    action: 'create_consult_dto_failed',
                    metadata: {
                        errorMessage:
                            error instanceof Error
                                ? error.message
                                : String(error),
                        errorName:
                            error instanceof Error ? error.name : undefined,
                    },
                }
            )

            logEvent('ERROR', 'create_consultDto', 'Create ConsultDto failed', {
                error: error,
            })

            throw error
        }
    }

    async updateForState(consult: ConsultDto): Promise<ConsultDto> {
        const version = await fetchVersion('ConsultDto', consult.id)

        const updatedConsult = await executeMutation<ConsultDto>(
            updateConsultDtoMutation,
            {
                input: {
                    id: consult.id,
                    state: consult.state,
                    _version: version,
                },
            },
            'updateConsultDto'
        )

        return updatedConsult
    }

    async updateForAudioAttachment(
        id: string,
        audioId: string,
        state: string,
        accuracy: string
    ): Promise<ConsultDto> {
        const version = await fetchVersion('ConsultDto', id)

        const updatedConsult = await executeMutation<ConsultDto>(
            updateConsultDtoMutation,
            {
                input: {
                    id,
                    audioId,
                    state,
                    accuracy,
                    _version: version,
                },
            },
            'updateConsultDto'
        )

        logger.info('Consult prepared for audio attachment processing', {
            action: 'update_consult_audio_attachment',
            metadata: { consultId: id, audioId, state, accuracy },
        })
        return updatedConsult
    }

    async updateEntities(
        id: string,
        entities: EntityDto[]
    ): Promise<ConsultDto> {
        const version = await fetchVersion('ConsultDto', id)

        const updatedConsult = await executeMutation<ConsultDto>(
            updateConsultDtoMutation,
            { input: { id, entities, _version: version } },
            'updateConsultDto'
        )

        logger.info('Consult entities updated successfully', {
            action: 'update_consult_entities',
            metadata: { consultId: id, entityCount: entities.length },
        })
        return updatedConsult
    }

    async updatePatient(id: string, patient: PatientDto) {
        logger.debug('Updating patient for consult', {
            action: 'update_consult_patient',
            metadata: { consultId: id, patientId: patient.id },
        })

        try {
            // Get the preferred name or first name from the names array
            const preferredName =
                patient.names?.find((n) => n?.preferred) ?? patient.names?.[0]
            const givenName = preferredName?.givenNames?.[0] || ''
            const familyName = preferredName?.familyName || ''
            const title = `${givenName} ${familyName}`.trim()

            const version = await fetchVersion('ConsultDto', id)

            await executeMutation(
                updateConsultDtoMutation,
                {
                    input: {
                        id,
                        patientId: patient.id,
                        title,
                        _version: version,
                    },
                },
                'updateConsultDto'
            )

            logger.info('Patient data updated successfully', {
                action: 'update_patient',
                metadata: { consultId: id, hasPatientId: !!patient.id },
            })

            // Save patient data to ConsultPatientDto table (new architecture)
            await consultPatientRepository.savePatient(
                id,
                patient.id,
                patient,
                null // matchedPatients not needed when attaching
            )

            logger.info('Consult updated with patient successfully', {
                action: 'update_consult_patient_success',
                metadata: { consultId: id, patientId: patient.id },
            })
        } catch (error) {
            logger.error(
                'Error updating consult with patient',
                error as Error,
                {
                    action: 'update_consult_patient_error',
                    metadata: { consultId: id, patientId: patient.id },
                }
            )
            throw error
        }
    }

    async updateRating(id: string, rating: number): Promise<ConsultDto> {
        const version = await fetchVersion('ConsultDto', id)

        const updatedConsult = await executeMutation<ConsultDto>(
            updateConsultDtoMutation,
            { input: { id, rating, _version: version } },
            'updateConsultDto'
        )

        logger.info('Consult rating updated successfully', {
            action: 'update_consult_rating',
            metadata: { consultId: id, rating },
        })
        return updatedConsult
    }

    async updatePresignedUrls(
        id: string,
        attachments: AttachmentDto[]
    ): Promise<ConsultDto> {
        const version = await fetchVersion('ConsultDto', id)

        const updatedConsult = await executeMutation<ConsultDto>(
            updateConsultDtoMutation,
            { input: { id, attachments, _version: version } },
            'updateConsultDto'
        )

        logger.info('Consult presigned URLs updated successfully', {
            action: 'update_consult_presigned_urls',
            metadata: {
                consultId: id,
                attachmentCount: attachments.length,
            },
        })
        return updatedConsult
    }
}

export async function retryConsult(consultId: string): Promise<string> {
    const operation = ApiHelper.buildRetryConsult(consultId)
    const response =
        await ApiRequester.callGetOperationRequest<RetryConsultResponse>(
            operation
        )

    logger.debug('Retry consult response received', {
        action: 'retry_consult_response',
        metadata: {
            consultId,
            success: response.success,
            message: response.message,
        },
    })

    return response.message
}
